/**
 * API Error Handling Utilities
 *
 * See docs/conventions.md for usage patterns.
 */

/**
 * The Auth0 logout function. Accepts optional logout params; callers throughout
 * the app pass the raw `useAuth0().logout`, which is why `returnTo` is applied
 * here rather than trusting each call site to set it.
 */
export type LogoutFn = (options?: {
  logoutParams?: { returnTo?: string };
}) => void;

/**
 * Handles API response errors, specifically checking for authentication failures.
 * When a 401 is detected, triggers logout and returns true to signal the caller
 * should stop processing.
 *
 * The logout always sets `returnTo` to the current origin. Without it, Auth0
 * falls back to the first configured Allowed Logout URL (the dev
 * `http://127.0.0.1:3000`), sending prod users to localhost after a forced
 * logout on token expiry.
 *
 * @param response - The fetch Response object
 * @param logout - The Auth0 logout function
 * @returns true if an auth error was handled (caller should bail), false otherwise
 *
 * @example
 * ```typescript
 * const response = await fetch('/api/endpoint', { headers });
 * if (!response.ok) {
 *   if (handleApiError(response, logout)) return;
 *   throw new Error('Request failed');
 * }
 * ```
 */
export function handleApiError(
  response: Response,
  logout: LogoutFn
): boolean {
  if (response.status === 401) {
    // Token expired or invalid - force re-authentication
    logout({ logoutParams: { returnTo: window.location.origin } });
    return true;
  }
  return false;
}

/**
 * Checks if an error is an Auth0 "Login required" error.
 * This happens when getAccessTokenSilently() is called but the session
 * has expired or the user isn't authenticated.
 *
 * @param error - The error to check
 * @returns true if this is a login required error
 *
 * @example
 * ```typescript
 * try {
 *   const token = await getAccessTokenSilently();
 * } catch (err) {
 *   if (isLoginRequiredError(err)) {
 *     loginWithRedirect();
 *     return;
 *   }
 *   throw err;
 * }
 * ```
 */
export function isLoginRequiredError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.message.includes('Login required') ||
           error.message.includes('login_required');
  }
  return false;
}
